/**
 * Workspace Not Found page.
 * Shown when URL handle doesn't match any accessible workspace.
 * Lists user's available workspaces with links.
 */

import { Link } from "react-router-dom";
import { publicRoutes } from "../lib/routes";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { Building2, AlertCircle, ArrowRight } from "lucide-react";

interface WorkspaceNotFoundProps {
  handle: string;
}

export function WorkspaceNotFound({ handle }: WorkspaceNotFoundProps) {
  const { workspaces, isLoading } = useWorkspace();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-lg p-8">
          {/* Error Icon */}
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>

          {/* Error Message */}
          <h1 className="text-2xl font-bold text-foreground text-center mb-2">
            Workspace Not Found
          </h1>
          <p className="text-muted-foreground text-center mb-6">
            The workspace <span className="font-mono text-foreground">"{handle}"</span> doesn't exist
            or you don't have access to it.
          </p>

          {/* Available Workspaces */}
          {workspaces.length > 0 ? (
            <div>
              <h2 className="text-sm font-medium text-foreground mb-3">
                Your workspaces:
              </h2>
              <div className="space-y-2">
                {workspaces.map((workspace) => (
                  <Link
                    key={workspace.id}
                    to={workspace.handle ? `/ws/${workspace.handle}/dashboard` : publicRoutes.home}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-accent transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">
                          {workspace.name}
                        </p>
                        {workspace.handle && (
                          <p className="text-sm text-muted-foreground font-mono">
                            /{workspace.handle}
                          </p>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-muted-foreground mb-4">
                You don't have access to any workspaces yet.
              </p>
              <Link
                to={publicRoutes.home}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create a Workspace
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
